import {
  RealtimeAckMessage,
  RealtimeClientCommand,
  RealtimeClientCommandName,
  RealtimeClientCommandPayloads,
  RealtimeCursor,
  RealtimeEntityCommandPayload,
  RealtimeFeatureCommandPayload,
  RealtimeCommandQueuePayload,
  RealtimeServerToClientMessage,
} from '@common/api/realtime';

export {
  RealtimeAckError,
  RealtimeAckMessage,
  RealtimeAckResponse,
  toRealtimeAckError,
  RealtimeClientCommand,
  RealtimeClientCommandName,
  RealtimeClientCommandPayloads,
  RealtimeCommandCompletedPayload,
  RealtimeCommandRejectedPayload,
  RealtimeCursor,
  RealtimeEntityCommandPayload,
  RealtimeFeatureCommandPayload,
  RealtimeServerToClientMessage,
} from '@common/api/realtime';

export const REALTIME_V2_COMMAND_TIMEOUT_MS = 30000;

// Timeout oltre il quale, se lo scope globale non raggiunge lo stato `ready`, si forza il logout:
// non si entra MAI in un progetto senza le entita' di base (es. metainterfacce) sincronizzate.
export const REALTIME_V2_GLOBAL_SCOPE_READY_TIMEOUT_MS = 60000;

// Retry lato client quando il server risponde `busy` (coda di ammissione piena): backoff esponenziale
// con full jitter, entro il timeout complessivo del comando.
export const REALTIME_V2_BUSY_MAX_RETRIES = 8;
export const REALTIME_V2_BUSY_BASE_DELAY_MS = 500;
export const REALTIME_V2_BUSY_MAX_DELAY_MS = 8000;

/**
 * Backpressure lato client sui comandi di ingresso scope (enterHome/enterProject/enterIssue/
 * enterLinkedPreview), gli stessi che il backend sottopone a controllo di ammissione.
 *
 * Il caso che ha motivato questa coda: un ticket con N task linkati monta N linked-preview nello
 * stesso tick e sparava N `enterLinkedPreview` insieme. Oltre il cap per-connessione del server i
 * comandi in eccesso venivano rifiutati ISTANTANEAMENTE con `connection_limit` (non entravano
 * nemmeno in coda), i retry ripartivano tutti insieme e dopo pochi round la preview si arrendeva.
 *
 * Qui teniamo una finestra di invio costante: non più di `MAX_INFLIGHT` ingressi in volo verso il
 * server, gli altri aspettano in una FIFO locale. Il valore deve restare <=
 * REALTIME_ADMISSION_MAX_PER_CONNECTION lato backend, altrimenti la coda locale non serve a nulla.
 */
export const REALTIME_V2_MAX_INFLIGHT_THROTTLED_COMMANDS = 3;

/** Spaziatura minima fra due invii consecutivi di comandi di ingresso scope (anti-raffica). */
export const REALTIME_V2_THROTTLED_COMMAND_MIN_GAP_MS = 60;

/**
 * Budget complessivo di un comando di ingresso scope: deve coprire l'attesa nella coda locale, la
 * coda di ammissione del server e i retry su `busy`. Il timeout standard dei comandi è pensato per
 * una singola andata/ritorno e qui sarebbe troppo stretto.
 */
export const REALTIME_V2_THROTTLED_COMMAND_TIMEOUT_MS = 120000;

export const REALTIME_V2_SCOPE_KEYS = {
  GLOBAL: 'global',
  PROJECT_PREFIX: 'project:',
  ISSUE_PREFIX: 'issue:',
  LINKED_PREVIEW_PREFIX: 'linked-preview:',
} as const;

export type RealtimeV2ScopeKey =
  | typeof REALTIME_V2_SCOPE_KEYS.GLOBAL
  | `${typeof REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX}${string}`
  | `${typeof REALTIME_V2_SCOPE_KEYS.ISSUE_PREFIX}${string}`
  | `${typeof REALTIME_V2_SCOPE_KEYS.LINKED_PREVIEW_PREFIX}${string}`;

export interface RealtimeV2CommandInput<TCommand extends RealtimeClientCommandName = RealtimeClientCommandName> {
  id?: string;
  type: TCommand;
  payload: RealtimeClientCommandPayloads[TCommand];
  expectsCompletion?: boolean;
  waitForCompletion?: boolean;
  timeoutMs?: number;
}

export type RealtimeV2CommandOptions = Pick<RealtimeV2CommandInput, 'id' | 'expectsCompletion' | 'waitForCompletion' | 'timeoutMs'>;

export interface RealtimeV2CommandResult<TBody = any> {
  requestId: string;
  command: RealtimeClientCommandName;
  accepted: boolean;
  completed: boolean;
  body?: TBody;
}

/**
 * Stato di attesa di un singolo scope in ammissione, indicizzato per `scopeKey`. È il tipo che la
 * UI consuma per mostrare la coda. Rappresenta ESCLUSIVAMENTE l'attesa in coda sul server: appena
 * lo slot viene concesso (`accepted`) l'attesa termina e il sync prosegue col contenuto normale.
 * Deriva dai campi della coda del server (position/queued/...) più lo scope di riferimento.
 */
export type RealtimeV2AdmissionEntry = RealtimeCommandQueuePayload & {
  scopeKey: string;
};

/**
 * Eventi "grezzi" di ammissione emessi dal socket (primitiva di trasporto), già correlati allo
 * `scopeKey` di destinazione. Il socket NON mantiene stato: si limita a inoltrare questi segnali;
 * la macchina a stati vive in RealtimeV2AdmissionService.
 *  - `queued`   → il comando è in coda sul server (o la posizione è cambiata);
 *  - `accepted` → il server ha concesso lo slot: l'attesa in coda termina (il sync prosegue a parte);
 *  - `ready`    → lo scope ha completato il primo sync: eventuale attesa residua chiusa;
 *  - `settled`  → il comando è terminato senza ready utile (reject finale o completed): attesa chiusa.
 */
export type RealtimeV2AdmissionEvent =
  | ({ kind: 'queued'; scopeKey: string } & RealtimeCommandQueuePayload)
  | { kind: 'accepted'; scopeKey: string; requestId: string; command: RealtimeClientCommandName }
  | { kind: 'ready'; scopeKey: string }
  | { kind: 'settled'; scopeKey: string; requestId?: string; command?: RealtimeClientCommandName };

/**
 * Ricostruisce lo `scopeKey` di un comando di ingresso soggetto ad ammissione, a partire dal suo
 * payload. Rispecchia il mapping del backend (session-orchestrator): enterHome → `global`. Ritorna
 * `null` per i comandi non soggetti ad ammissione (leave*, entityCommand, featureCommand).
 */
export function resolveThrottledScopeKey(
  command: RealtimeClientCommandName,
  payload: RealtimeClientCommandPayloads[RealtimeClientCommandName],
): RealtimeV2ScopeKey | null {
  switch (command) {
    case 'enterHome':
      return REALTIME_V2_SCOPE_KEYS.GLOBAL;
    case 'enterProject':
      return `${REALTIME_V2_SCOPE_KEYS.PROJECT_PREFIX}${(payload as RealtimeClientCommandPayloads['enterProject']).projectId}`;
    case 'enterIssue':
      return `${REALTIME_V2_SCOPE_KEYS.ISSUE_PREFIX}${(payload as RealtimeClientCommandPayloads['enterIssue']).issueId}`;
    case 'enterLinkedPreview': {
      const preview = payload as RealtimeClientCommandPayloads['enterLinkedPreview'];
      const previewKey = preview.previewKey ?? preview.linkedIssueId;
      return `${REALTIME_V2_SCOPE_KEYS.LINKED_PREVIEW_PREFIX}${previewKey}`;
    }
    default:
      return null;
  }
}

export type RealtimeV2PendingCommand = {
  command: RealtimeClientCommandName;
  // Envelope completo, conservato per poter ri-inviare il comando sui retry `busy`.
  envelope: RealtimeClientCommand;
  waitForCompletion: boolean;
  timeoutId: ReturnType<typeof setTimeout>;
  resolve: (result: RealtimeV2CommandResult) => void;
  reject: (error: RealtimeV2CommandFailure) => void;
  // Stato dei retry su `busy`.
  attempts?: number;
  retryTimer?: ReturnType<typeof setTimeout>;
  busyCounted?: boolean;
  // Valorizzato solo per i comandi soggetti a coda locale (ingressi di scope): serve a coalescere
  // gli ingressi duplicati e a contabilizzare gli slot della finestra di invio.
  scopeKey?: string | null;
  // True mentre il comando occupa uno slot della finestra di invio locale.
  inFlight?: boolean;
};

/**
 * Callback di risposta a un `ws_req`. In caso di rifiuto (`ok: false`) il chiamante DEVE passare
 * l'errore che ha causato il rifiuto (Error, stringa o oggetto): viene normalizzato e inviato al
 * backend, che lo riporta in telemetria a corredo dell'evento di ack negativo.
 */
export type RealtimeV2RequestAck = (ok: boolean, error?: unknown, context?: Record<string, any>) => void;

export type RealtimeV2RequestHandler = (
  request: RealtimeAckMessage,
  ack: RealtimeV2RequestAck,
) => void | Promise<void>;

export interface RealtimeV2CommandFailure {
  message: string;
  code: string;
  details?: Record<string, any>;
}
